import type { CSSProperties } from 'react'
import { Users } from 'lucide-react'
import { avatarUrl } from '@/lib/avatar'
import { publicUser } from '@/lib/auth'
import { cx } from '@/lib/cx'
import { useAuthStore } from '@/store/auth'
import { displayName } from '@/store/social'
import type { User } from '@/types/api'
import { Avatar } from './Avatar'
import styles from './GroupAvatar.module.css'

const colorOf = (id: string) => `hsl(${Number(id.slice(-4)) % 360} 55% 50%)`

interface Props {
  /** Собеседники группы — без себя, в том же порядке, что и в названии группы. */
  recipients: User[] | undefined
  size?: number
  className?: string | undefined
}

/**
 * Лицо группы, как у Discord: две аватарки внахлёст — первый собеседник слева сверху, второй справа
 * снизу. Собеседник один — вторым встаёт своё лицо: группа остаётся группой, а не похожа на беседу один
 * на один. Под нижней аватаркой из верхней вырезан круг с зазором — маской, а не обводкой цветом фона,
 * поэтому стопка одинакова на строке при наведении, на выбранной и в окне. Рамок у стопки нет, как у
 * Discord. Никого, кроме себя, — значок группы.
 */
export function GroupAvatar({ recipients, size = 32, className }: Props) {
  const me = useAuthStore((s) => s.user)
  const others = recipients ?? []
  const faces = others.length >= 2 ? others.slice(0, 2) : others[0] && me ? [publicUser(me), others[0]] : null
  if (!faces) {
    return (
      <span className={cx(styles.stack, styles.icon, className)} style={{ '--stack': `${size}px` } as CSSProperties} aria-hidden="true">
        <Users size={Math.round(size * 0.56)} />
      </span>
    )
  }
  const inner = Math.round(size * 0.7)
  const style = { '--stack': `${size}px`, '--inner': `${inner}px`, '--gap': `${Math.max(2, Math.round(size / 16))}px` } as CSSProperties
  return (
    <span className={cx(styles.stack, className)} style={style} aria-hidden="true">
      {faces.map((u, i) => (
        <Avatar
          key={u.id}
          initial={displayName(u).slice(0, 1).toUpperCase()}
          color={colorOf(u.id)}
          size={inner}
          src={avatarUrl(u.id, u.avatar)}
          className={i === 0 ? styles.back : styles.front}
        />
      ))}
    </span>
  )
}
